/**
 * Caminho público (`/…`) para um documento `pages` a partir de dados populados
 * (referências Lexical, CMSLink, menus). Usa `fullPath` quando existe para
 * hierarquia (`/pai/filho`); caso contrário cai no `slug` (página raiz).
 */
export function hrefFromPageDoc(doc: {
  slug?: string | null
  fullPath?: string | null
} | null | undefined): string {
  if (!doc) return '/'
  const trimSlashes = (s: string) => s.replace(/^\/+/u, '').replace(/\/+$/u, '').trim()
  const fp = typeof doc.fullPath === 'string' ? trimSlashes(doc.fullPath) : ''
  if (fp) {
    if (fp === 'home') return '/'
    return `/${fp}`
  }
  const slug = typeof doc.slug === 'string' ? trimSlashes(doc.slug) : ''
  if (!slug || slug === 'home') return '/'
  return `/${slug}`
}

/**
 * Igual à de cima, mas prefere o SLUG TRADUZIDO do idioma ativo (`localizedSlug`) quando a página é
 * de nível raiz. Feita para os MENUS (ver `resolveHeaderNavigation`), onde o documento já chega
 * populado no locale do pedido — logo o `localizedSlug` já vem no idioma certo, de graça.
 *
 * O problema que resolve: o menu apontava sempre para o caminho em português (`/en/quem-somos`) e o
 * site respondia 308 até o endereço traduzido (`/en/who-we-are`). Funcionava para o leitor, mas
 * eram 18 redirecionamentos (6 idiomas × 3 links) gastando rastreio do Google à toa.
 *
 * ⚠️ SÓ NÍVEL RAIZ, de propósito. Uma página com pai precisa do caminho inteiro traduzido — e a
 * regra do projeto (`computeLocalizedFullPath`) é que o caminho só é válido quando TODOS os
 * segmentos têm slug no idioma, o que exige subir a cadeia de ancestrais com uma consulta por
 * nível. Isso é assíncrono, e a montagem de menu é síncrona e serve QUATRO menus em produção
 * (principal, móvel, topo e rodapé). Para páginas com pai, cai no comportamento de sempre.
 *
 * Aditiva: sem `localizedSlug`, o resultado é byte a byte o de `hrefFromPageDoc`.
 */
export function hrefFromMenuPageDoc(
  doc:
    | {
        slug?: string | null
        fullPath?: string | null
        localizedSlug?: string | null
      }
    | null
    | undefined,
): string {
  if (!doc) return '/'
  const trimSlashes = (s: string) => s.replace(/^\/+/u, '').replace(/\/+$/u, '').trim()
  const localized = typeof doc.localizedSlug === 'string' ? trimSlashes(doc.localizedSlug) : ''
  if (localized && localized !== 'home') {
    const fp = typeof doc.fullPath === 'string' ? trimSlashes(doc.fullPath) : ''
    // `fullPath` com barra = tem ancestral ⇒ não dá para traduzir só o último segmento.
    const ehRaiz = !fp || !fp.includes('/')
    if (ehRaiz) return `/${localized}`
  }
  return hrefFromPageDoc(doc)
}
